import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "~/components/ui/carousel";
import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { ScrollArea } from "~/components/ui/scroll-area";
import useEmblaCarousel from "embla-carousel-react";
import { X } from "lucide-react";
import Autoplay from "embla-carousel-autoplay";
import { AmenitySection } from "~/components/AmenitySection";
import { RoomWithImages } from "~/lib/server-functions/get-locations";
import { WifiIcon } from "~/icons/WifiIcon";
import { PowerIcon } from "~/icons/PowerIcon";
import { RadioIcon } from "~/icons/RadioIcon";
import { Lamp2Icon } from "~/icons/Lamp2Icon";
import { StageIcon } from "~/icons/StageIcon";
import { CheckIcon } from "~/icons/CheckIcon";
import { XIcon as CrossIcon } from "~/icons/XIcon";
import { PartyIcon } from "~/icons/PartyIcon";
import { TheatreSeatingIcon } from "~/icons/TheatreSeatingIcon";
import { ClassroomSeatingIcon } from "~/icons/ClassroomSeatingIcon";
import { BlockSeatingIcon } from "~/icons/BlockSeatingIcon";
import { UFormSeatingIcon } from "~/icons/UFormSeatingIcon";
import { BanquetSeatingIcon } from "~/icons/BanquetSeatingIcon";
import { TableSeatingIcon } from "~/icons/TableSeatingIcon";
import { StandingSeatingIcon } from "~/icons/StandingSeatingIcon";
import { BuildingIcon } from "~/icons/BuildingIcon";
import { SubSection } from "~/components/ContentElements/SubSection";
import { BoxIcon } from "~/icons/BoxIcon";
import { HorizontalDoubleArrowIcon } from "~/icons/HorizontalDoubleArrowIcon";
import { OpenSquareIcon } from "~/icons/OpenSquareIcon";
import { VerticalDoubleArrowIcon } from "~/icons/VerticalDoubleArrowIcon";
import { Button } from "~/components/ui/button";
import { RichTextRenderer } from "~/components/RichTextRenderer";
import { formatGermanCurrency } from "../ui/price-input";
import {
  IMAGE_SIZES,
  buildImageUrl,
  formatGermanNumberWithoutDecimals,
  convertCmToMeters,
} from "~/lib/utils";

export default function RoomPopUp({
  open,
  onOpenChange,
  onOpenGallery,
  images,
  sections = [{ id: -1, label: "Alle Bilder" }],
  activeImageId,
  room,
}: {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  onOpenGallery: (value: boolean) => void;
  images: {
    id: number | null;
    path: string;
    mimetype: string | null;
    alt: string | null;
    size: number | null;
    sort: number | null;
    category: string | null;
    categoryId: number | null;
  }[];
  sections?: { id: number; label: string }[];
  activeImageId?: string;
  room: RoomWithImages;
}) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [activeImage, setActiveImage] = useState(
    activeImageId ?? images[0]?.id ?? null,
  );
  const [activeTab, setActiveTab] = useState(sections[0].id);

  // Filter and sort images based on activeTab
  const filteredImages = (() => {
    const filtered =
      activeTab === -1
        ? images
        : images.filter((img) => img.categoryId === activeTab);
    return [...filtered].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
  })();

  // Update active image when images, activeTab, or activeImageId changes
  useEffect(() => {
    const filtered =
      activeTab === -1
        ? images
        : images.filter((img) => img.categoryId === activeTab);
    const sorted = [...filtered].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

    if (sorted.length > 0) {
      setActiveImage(activeImageId ?? sorted[0].id ?? null);
    }
  }, [images, activeTab, activeImageId]);

  const layouts = {
    party: room.seatingParty || room.capacityPartySeating || null,
    row: room.seatingRow || room.capacityRowSeating || null,
    parliament:
      room.seatingParliamentary || room.capacityClassroomSeating || null,
    block: room.seatingBlock || room.capacityBlockSeating || null,
    uform: room.seatingUShape || room.capacityUSeating || null,
    banquet: room.seatingBanquet || room.capacityBanquetSeating || null,
    table: room.seatingTable || room.capacityTableSeating || null,
    standing: room.seatingStanding || null,
  } as const;

  const layoutConfig = [
    { key: "party", label: "Party", icon: <PartyIcon /> },
    { key: "row", label: "Reihe", icon: <TheatreSeatingIcon /> },
    { key: "parliament", label: "Parlament", icon: <ClassroomSeatingIcon /> },
    { key: "block", label: "Block", icon: <BlockSeatingIcon /> },
    { key: "uform", label: "U-Form", icon: <UFormSeatingIcon /> },
    { key: "banquet", label: "Bankett", icon: <BanquetSeatingIcon /> },
    { key: "table", label: "Tisch", icon: <TableSeatingIcon /> },
    { key: "standing", label: "Stehend", icon: <StandingSeatingIcon /> },
  ] as const;

  const handleCategoryChange = (id: number) => {
    setActiveTab(id);
  };

  useEffect(() => {
    if (!carouselApi) return;

    const updateCarouselState = () => {
      if (!filteredImages.length) return;

      setActiveImage(filteredImages[carouselApi.selectedScrollSnap()].id);
    };

    updateCarouselState();

    carouselApi.on("select", updateCarouselState);

    return () => {
      carouselApi.off("select", updateCarouselState); // Clean up on unmount
    };
  }, [carouselApi, filteredImages]);

  useEffect(() => {
    setActiveImage(activeImageId ?? images[0]?.id);
  }, [activeImageId]);

  const scrollToIndex = (index: number) => {
    carouselApi?.scrollTo(index);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDescription className="sr-only">Raumdetails</DialogDescription>
      <DialogContent
        className="sm:max-w-[80vw] max-h-[90vh] p-0 border-0 rounded-lg"
        overlayIntensity="dark"
        showCloseButton={false}
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="inline-flex h-10 w-10 absolute cursor-pointer top-4 right-4 z-20 items-center justify-center rounded-full bg-dove-gray-darker text-white hover:bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black"
        >
          <X className="h-4 w-4" />
        </button>
        <ScrollArea className="h-[90vh] w-full">
          <div className="relative w-full">
            <Button
              variant="outline"
              className="absolute bottom-4 right-4 z-10 border-white text-white"
              onClick={() => {
                onOpenGallery(true);
              }}
            >
              Alle Bilder
            </Button>

            {/* Slide counter below center over carousel */}
            {filteredImages.length > 0 ? (
              <>
                {filteredImages.length > 1 && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 bottom-6 z-20 px-4 py-1 rounded bg-dove-gray-darker text-white text-base font-medium"
                    style={{ pointerEvents: "none" }}
                  >
                    {(carouselApi?.selectedScrollSnap?.() ?? 0) + 1}/
                    {filteredImages.length}
                  </div>
                )}

                <Carousel
                  setApi={setCarouselApi}
                  opts={{
                    loop: filteredImages.length > 1,
                  }}
                  plugins={[
                    ...(filteredImages.length > 1
                      ? [
                        Autoplay({
                          delay: 10000,
                        }),
                      ]
                      : []),
                  ]}
                >
                  <CarouselContent>
                    {filteredImages.map((img, i) => (
                      <CarouselItem key={img.id ?? i}>
                        <div className="relative h-[450px] lg:h-[900px] flex items-center justify-center">
                          <img
                            src={buildImageUrl(img.path, {
                              size: IMAGE_SIZES.galleryLarge,
                            })}
                            alt={img.alt || ""}
                            className="absolute top-0 left-0 w-full h-full object-cover z-0 rounded-t-lg"
                          />
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>

                  {filteredImages.length > 1 && (
                    <>
                      <CarouselPrevious
                        variant="flatWhite"
                        className="rounded shadow-[0_4px_10px_0_rgba(0,0,0,0.25)] absolute left-8 top-1/2 -translate-y-1/2"
                      />
                      <CarouselNext
                        variant="flatWhite"
                        className="rounded shadow-[0_4px_10px_0_rgba(0,0,0,0.25)] absolute right-8 top-1/2 -translate-y-1/2"
                      />
                    </>
                  )}
                </Carousel>
              </>
            ) : null}
          </div>

          <div className="px-6 pb-10 pt-6 layout-content-grid">
            <div className="flex flex-col gap-2">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <DialogTitle className="text-2xl font-semibold">
                    {room.name}
                  </DialogTitle>
                </div>
              </div>

              <div className="flex flex-wrap gap-8 py-4">
                {room.roomSize || room.maxArea ? (
                  <div className="flex flex-col items-center justify-between text-center">
                    <div>
                      <BoxIcon />
                    </div>
                    <div>
                      <div className="text-lg font-semibold">
                        {room.roomSize
                          ? formatGermanNumberWithoutDecimals(
                            convertCmToMeters(room.roomSize),
                          )
                          : room.maxArea
                            ? formatGermanNumberWithoutDecimals(
                              convertCmToMeters(room.maxArea),
                            )
                            : 0}
                      </div>
                      <div className="text-xs">m² Fläche</div>
                    </div>
                  </div>
                ) : null}

                {room.maxLength ? (
                  <div className="flex flex-col items-center justify-between text-center">
                    <div className="text-3xl">
                      <HorizontalDoubleArrowIcon />
                    </div>
                    <div>
                      <div className="text-lg font-semibold">
                        {formatGermanNumberWithoutDecimals(
                          convertCmToMeters(room.maxLength),
                        )}
                      </div>
                      <div className="text-xs">m Länge</div>
                    </div>
                  </div>
                ) : null}
                {room.maxWidth ? (
                  <div className="flex flex-col items-center justify-between text-center">
                    <div className="text-3xl">
                      <OpenSquareIcon />
                    </div>
                    <div>
                      <div className="text-lg font-semibold">
                        {formatGermanNumberWithoutDecimals(
                          convertCmToMeters(room.maxWidth),
                        )}
                      </div>
                      <div className="text-xs">m Breite</div>
                    </div>
                  </div>
                ) : null}
                {room.maxHeight ? (
                  <div className="flex flex-col items-center justify-between text-center">
                    <div className="text-3xl">
                      <VerticalDoubleArrowIcon />
                    </div>
                    <div>
                      <div className="text-lg font-semibold">
                        {formatGermanNumberWithoutDecimals(
                          convertCmToMeters(room.maxHeight),
                        )}
                      </div>
                      <div className="text-xs">m Höhe</div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <SubSection>
              <RichTextRenderer richText={room.longDescription?.german ?? ""} />
            </SubSection>

            {(room.areaType === "indoor" ||
              room.areaType === "outdoor" ||
              room.areaType === "mixed" ||
              room.separable ||
              room.roomShape) ? (
              <SubSection title="Raumdetails">
                <div className="flex flex-col gap-y-4">
                  {room.areaType === "indoor" ? (
                    <div className="flex flex-1 max-w-[150px] items-center justify-between">
                      <span>Indoor</span>
                      <CheckIcon />
                    </div>
                  ) : null}
                  {room.areaType === "outdoor" ? (
                    <div className="flex flex-1 max-w-[150px] items-center justify-between">
                      <span>Outdoor</span>
                      <CheckIcon />
                    </div>
                  ) : null}
                  {room.areaType === "mixed" ? (
                    <div className="flex flex-1 max-w-[150px] items-center justify-between">
                      <span>Indoor & Outdoor</span>
                      <CheckIcon />
                    </div>
                  ) : null}
                  {room.separable ? (
                    <div className="flex flex-1 max-w-[150px] items-center justify-between">
                      <span>Raum abtrennbar</span>
                      <CheckIcon />
                    </div>
                  ) : null}
                  {room.roomShape ? (
                    <div className="flex flex-1 max-w-[150px] items-center justify-between">
                      <span>Raumform:</span>
                      {room.roomShape.name}
                    </div>
                  ) : null}
                </div>
              </SubSection>
            ) : null}

            <SubSection title="Kapazitäten">
              <div className="flex flex-wrap gap-8 py-4">
                {layoutConfig.map((layout) => {
                  const value =
                    layouts[layout.key as keyof typeof layouts] ?? 0;
                  return (
                    <div
                      key={layout.key}
                      className="flex flex-col items-center justify-center text-center [&_svg]:size-14"
                    >
                      <div className="mb-4">{layout.label}</div>
                      <div className="mb-4">{layout.icon}</div>
                      <div className="text-lg">{value}</div>
                    </div>
                  );
                })}
              </div>
            </SubSection>

            <div className="flex flex-col gap-4 md:flex-row md:gap-12">
              <SubSection
                title="Mietkonditionen"
                className="md:min-w-0 md:flex-1"
              >
                {room?.priceIsPublic === true ? (
                  (() => {
                    const items = [
                      {
                        label: "Basismietpreis",
                        value: room?.rateDaily,
                        format: (v: number) =>
                          `${formatGermanCurrency(v / 100)} € / Tag`,
                      },
                      {
                        label: "Stundenlimit",
                        value: room?.rateDailyHourLimit,
                        format: (v: number) =>
                          v === 0 ? "Unbeschränkt" : `${formatGermanNumberWithoutDecimals(v)} Std.`,
                      },
                      {
                        label: "Stundenpreis",
                        value: room?.rateHourly,
                        format: (v: number) =>
                          `${formatGermanCurrency(v / 100)} € / Stunde`,
                      },
                      {
                        label: "Mindeststunden",
                        value: room?.rateHourlyMinimumHours,
                        format: (v: number) =>
                          `${formatGermanNumberWithoutDecimals(v)} Std.`,
                      },
                      {
                        label: "Pauschale pro Person",
                        value: room?.consumptionFlatRatePerPerson,
                        format: (v: number) =>
                          `${formatGermanCurrency(v / 100)} €`,
                      },
                      {
                        label: "Pauschale Mindestpersonen",
                        value: room?.consumptionFlatRateMinimumPersons,
                        format: (v: number) =>
                          `${formatGermanNumberWithoutDecimals(v)} Personen`,
                      },
                    ].filter((item) => item.value != null && item.value > 0);

                    if (items.length === 0) return null;

                    return (
                      <div className="space-y-2">
                        {items.map((item) => (
                          <div
                            key={item.label}
                            className="flex justify-between"
                          >
                            <strong>{item.label}</strong>
                            <span>{item.format(item.value!)}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()
                ) : (
                  <div className="flex flex-1 max-w-[250px] items-center justify-between">
                    <strong>Nur individuell auf Anfrage:</strong>
                    <CheckIcon />
                  </div>
                )}
                {room?.additionalCosts != null && room.additionalCosts > 0 ? (
                  <div className="flex flex-1 max-w-[250px] items-center justify-between">
                    <strong>Nebenkosten</strong>
                    <span>
                      {room.additionalCosts === -1
                        ? "Nach Verbrauch"
                        : `${formatGermanCurrency(
                          room.additionalCosts / 100,
                        )} €`}
                    </span>
                  </div>
                ) : null}
                {room.additionalPricingInformation?.german ? (
                  <div>
                    <strong>Sonstiges Preisinformationen</strong>
                    <RichTextRenderer
                      richText={room.additionalPricingInformation?.german ?? ""}
                    />
                  </div>
                ) : null}
                {room.additionalCostsInformation?.german ? (
                  <div>
                    <strong>Sonstiges Nebenkosten</strong>
                    <RichTextRenderer
                      richText={
                        room.additionalCostsInformation?.german ?? ""
                      }
                    />
                  </div>
                ) : null}
              </SubSection>

              <SubSection title="Verzehr" className="md:min-w-0 md:flex-1">
                {room?.priceIsPublic === true ? (
                  <div className="space-y-2">
                    {[
                      {
                        label: room?.minimumConsumptionPersons === -1 ? "Mindestverzehr Pauschale" : "Mindestverzehr pro Person",
                        value: room?.minimumConsumptionPrice,
                        format: (v: number) =>
                          `${formatGermanCurrency(v / 100)} €`,
                      },
                      {
                        label: "Mindestverzehr minimale Personenanzahl",
                        value: room?.minimumConsumptionPersons,
                        format: (v: number) => `${v} Personen`,
                      },
                    ]
                      .filter((item) => item.value != null && item.value > 0)
                      .map((item) => (
                        <div key={item.label} className="flex justify-between">
                          <strong>{item.label}</strong>
                          <span>{item.format(item.value!)}</span>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="flex flex-1 max-w-[250px] items-center justify-between">
                    <strong>Nur individuell auf Anfrage:</strong>
                    <CheckIcon />
                  </div>
                )}
              </SubSection>
            </div>

            <SubSection title="Ausstattung">
              {room?.groundLevel ||
                room?.accessible ||
                room?.daylight ||
                room?.accessibleForCars ||
                (room?.additionalAttributes?.some(
                  (attr) =>
                    attr.purpose === "location_room_information_attribute" &&
                    attr.active === true,
                ) ??
                  false) ? (
                <AmenitySection
                  title="Rauminformationen"
                  items={[
                    ...(room?.groundLevel
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Erdgeschoss",
                        },
                      ]
                      : []),
                    ...(room?.accessible
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "barrierefrei",
                        },
                      ]
                      : []),
                    ...(room?.daylight
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Tageslicht",
                        },
                      ]
                      : []),
                    ...(room?.accessibleForCars
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Mit KFZ befahrbar",
                        },
                      ]
                      : []),
                    ...(room?.additionalAttributes
                      ?.filter(
                        (attr) =>
                          attr.purpose ===
                          "location_room_information_attribute" &&
                          attr.active === true,
                      )
                      .map((attr) => ({
                        icon: <CheckIcon />,
                        text: attr.name?.german || attr.name?.english || "",
                      })) || []),
                  ]}
                />
              ) : null}

              {(room?.additionalAttributes?.some(
                (attr) =>
                  attr.purpose ===
                  "location_room_flooring_attribute" &&
                  attr.active === true,
              ) ??
                false) ? (
                <AmenitySection
                  title="Bodenbelag"
                  items={[
                    ...(room?.additionalAttributes
                      ?.filter(
                        (attr) =>
                          attr.purpose ===
                          "location_room_flooring_attribute" &&
                          attr.active === true,
                      )
                      .map((attr) => ({
                        icon: <CheckIcon />,
                        text: attr.name?.german || attr.name?.english || "",
                      })) || []),
                  ]}
                />
              ) : null}

              {room?.lighting ||
                room?.dimmableLighting ||
                room?.windows ||
                room?.blackoutCapable ||
                room?.heating ||
                room?.airConditioning ||
                room?.furnished ||
                room?.waterSupply ||
                room?.acPower ||
                room?.threePhasePower ||
                room?.stage ||
                room?.lectern ||
                room?.chairs ||
                (room?.additionalAttributes?.some(
                  (attr) =>
                    attr.purpose === "location_room_facility_attribute" &&
                    attr.active === true,
                ) ??
                  false) ? (
                <AmenitySection
                  title="Ausstattung"
                  items={[
                    ...(room?.lighting
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Licht",
                        },
                      ]
                      : []),
                    ...(room?.dimmableLighting
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Licht dimmbar",
                        },
                      ]
                      : []),
                    ...(room?.windows
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Fenster",
                        },
                      ]
                      : []),
                    ...(room?.blackoutCapable
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "verdunkelbar",
                        },
                      ]
                      : []),
                    ...(room?.heating
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Heizung",
                        },
                      ]
                      : []),
                    ...(room?.airConditioning
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Klimaanlage",
                        },
                      ]
                      : []),
                    ...(room?.furnished
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "möbliert",
                        },
                      ]
                      : []),
                    ...(room?.waterSupply
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Wasseranschluss",
                        },
                      ]
                      : []),
                    ...(room?.acPower
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Wechselstrom",
                        },
                      ]
                      : []),
                    ...(room?.threePhasePower
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Starkstrom",
                        },
                      ]
                      : []),
                    ...(room?.stage
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Bühne",
                        },
                      ]
                      : []),
                    ...(room?.lectern
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Rednerpult",
                        },
                      ]
                      : []),
                    ...(room?.additionalAttributes
                      ?.filter(
                        (attr) =>
                          attr.purpose === "location_room_facility_attribute" &&
                          attr.active === true,
                      )
                      .map((attr) => ({
                        icon: <CheckIcon />,
                        text: attr.name?.german || attr.name?.english || "",
                      })) || []),
                  ]}
                />
              ) : null}
              {room?.videoTechnology ||
                room?.soundSystem ||
                room?.publicAddressSystem ||
                room?.interpreterSystem ||
                room?.projectionTechnology ||
                room?.videoConference ||
                (room?.additionalAttributes?.some(
                  (attr) =>
                    attr.purpose === "location_room_event_technology_attribute" &&
                    attr.active === true,
                ) ??
                  false) ? (
                <AmenitySection
                  title="Eventtechnik"
                  items={[
                    ...(room?.videoTechnology
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Videotechnik",
                        },
                      ]
                      : []),
                    ...(room?.soundSystem
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Tonanlage",
                        },
                      ]
                      : []),
                    ...(room?.publicAddressSystem
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Sprechanlage",
                        },
                      ]
                      : []),
                    ...(room?.interpreterSystem
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Dolmetscheranlage",
                        },
                      ]
                      : []),
                    ...(room?.projectionTechnology
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Projektionstechnik",
                        },
                      ]
                      : []),
                    ...(room?.videoConference
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Videokonferenz",
                        },
                      ]
                      : []),
                    ...(room?.additionalAttributes
                      ?.filter(
                        (attr) =>
                          attr.purpose ===
                          "location_room_event_technology_attribute" &&
                          attr.active === true,
                      )
                      .map((attr) => ({
                        icon: <CheckIcon />,
                        text: attr.name?.german || attr.name?.english || "",
                      })) || []),
                  ]}
                />
              ) : null}
              {room?.wifi ||
                room?.lan ||
                room?.telephone ||
                room?.tvConnection ||
                room?.internet ||
                (room?.additionalAttributes?.some(
                  (attr) =>
                    attr.purpose ===
                    "location_room_communication_technology_attribute" &&
                    attr.active === true,
                ) ??
                  false) ? (
                <AmenitySection
                  title="Kommunikation"
                  items={[
                    ...(room?.wifi
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "WLAN",
                        },
                      ]
                      : []),
                    ...(room?.lan
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "LAN",
                        },
                      ]
                      : []),
                    ...(room?.telephone
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Telefon",
                        },
                      ]
                      : []),
                    ...(room?.tvConnection
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "TV-Anschluss",
                        },
                      ]
                      : []),
                    ...(room?.internet
                      ? [
                        {
                          icon: <CheckIcon />,
                          text: "Internet",
                        },
                      ]
                      : []),
                    ...(room?.additionalAttributes
                      ?.filter(
                        (attr) =>
                          attr.purpose ===
                          "location_room_communication_technology_attribute" &&
                          attr.active === true,
                      )
                      .map((attr) => ({
                        icon: <CheckIcon />,
                        text: attr.name?.german || attr.name?.english || "",
                      })) || []),
                  ]}
                />
              ) : null}

              {room.additionalEquipmentInformation?.german ? (
                <div>
                  <strong>Sonstiges</strong>
                  <RichTextRenderer
                    richText={room.additionalEquipmentInformation?.german ?? ""}
                  />
                </div>
              ) : null}
            </SubSection>

            {room?.onlyA1 ||
              room?.onlyB1 ||
              room?.noPyrotechnics ||
              room?.noOpenFire ||
              room?.absoluteSmokingBan ||
              room.additionalFireProtectionInformation?.german ||
              (room?.additionalAttributes?.some(
                (attr) =>
                  (attr.purpose === "location_room_decoration_attribute" ||
                    attr.purpose === "location_room_fire_protection_attribute" ||
                    attr.purpose === "location_room_smoking_attribute") &&
                  attr.active === true,
              ) ??
                false) ? (
              <SubSection title="Brandschutz">
                {room?.onlyA1 ||
                  room?.onlyB1 ||
                  (room?.additionalAttributes?.some(
                    (attr) =>
                      attr.purpose === "location_room_decoration_attribute" &&
                      attr.active === true,
                  ) ??
                    false) ? (
                  <AmenitySection
                    title="Dekoration"
                    items={[
                      ...(room?.onlyA1
                        ? [
                          {
                            icon: <CheckIcon />,
                            text: "nur A1 (nicht entflammbar)",
                          },
                        ]
                        : []),
                      ...(room?.onlyB1
                        ? [
                          {
                            icon: <CheckIcon />,
                            text: "nur B1 (schwer entflammbar)",
                          },
                        ]
                        : []),
                      ...(room?.additionalAttributes
                        ?.filter(
                          (attr) =>
                            attr.purpose ===
                            "location_room_decoration_attribute" &&
                            attr.active === true,
                        )
                        .map((attr) => ({
                          icon: <CheckIcon />,
                          text: attr.name?.german || attr.name?.english || "",
                        })) || []),
                    ]}
                  />
                ) : null}
                {room?.noPyrotechnics ||
                  room?.noOpenFire ||
                  (room?.additionalAttributes?.some(
                    (attr) =>
                      attr.purpose ===
                      "location_room_fire_protection_attribute" &&
                      attr.active === true,
                  ) ??
                    false) ? (
                  <AmenitySection
                    title="Feuer"
                    items={[
                      ...(room?.noPyrotechnics
                        ? [
                          {
                            icon: <CheckIcon />,
                            text: "keine Pyrotechnik",
                          },
                        ]
                        : []),
                      ...(room?.noOpenFire
                        ? [
                          {
                            icon: <CheckIcon />,
                            text: "kein offenes Feuer (Kerzen)",
                          },
                        ]
                        : []),
                      ...(room?.additionalAttributes
                        ?.filter(
                          (attr) =>
                            attr.purpose ===
                            "location_room_fire_protection_attribute" &&
                            attr.active === true,
                        )
                        .map((attr) => ({
                          icon: <CheckIcon />,
                          text: attr.name?.german || attr.name?.english || "",
                        })) || []),
                    ]}
                  />
                ) : null}
                {room?.absoluteSmokingBan ||
                  (room?.additionalAttributes?.some(
                    (attr) =>
                      attr.purpose === "location_room_smoking_attribute" &&
                      attr.active === true,
                  ) ??
                    false) ? (
                  <AmenitySection
                    title="Rauchen"
                    items={[
                      ...(room?.absoluteSmokingBan
                        ? [
                          {
                            icon: <CheckIcon />,
                            text: "absolutes Rauchverbot",
                          },
                        ]
                        : []),
                      ...(room?.additionalAttributes
                        ?.filter(
                          (attr) =>
                            attr.purpose ===
                            "location_room_smoking_attribute" &&
                            attr.active === true,
                        )
                        .map((attr) => ({
                          icon: <CheckIcon />,
                          text: attr.name?.german || attr.name?.english || "",
                        })) || []),
                    ]}
                  />
                ) : null}
                {room.additionalFireProtectionInformation?.german ? (
                  <div>
                    <strong>Sonstiges</strong>
                    <RichTextRenderer
                      richText={
                        room.additionalFireProtectionInformation?.german ?? ""
                      }
                    />
                  </div>
                ) : null}
              </SubSection>
            ) : null}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
